export type SceneBinaryAssetSlot = 'background' | 'foreground' | 'thumbnail' | 'traversal-mask';

const LEVEL_DESIGN_STORAGE_PREFIX = 'users/';
const SCENE_STORAGE_PREFIX = 'scenes/';
const IMAGE_EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};
const IMAGE_CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};
const STILL_IMAGE_CONTENT_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const trimLeadingDot = (value: string): string => value.replace(/^\.+/, '').toLowerCase();

const toLastPathSegment = (value: string): string => {
  const withoutQuery = value.split(/[?#]/, 1)[0] ?? value;
  const segments = withoutQuery.split('/');
  return segments[segments.length - 1] ?? withoutQuery;
};

const toExtensionFromFileName = (value: string | null | undefined): string | null => {
  const fileName = typeof value === 'string' ? toLastPathSegment(value.trim()) : '';
  if (!fileName.includes('.')) {
    return null;
  }
  return trimLeadingDot(fileName.slice(fileName.lastIndexOf('.') + 1));
};

export const sanitizeStorageAssetId = (value: string, fallback = 'asset'): string => {
  const sanitized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return sanitized || fallback;
};

export const inferImageContentType = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const dataUrlMatch = trimmed.match(/^data:([^;,]+)[;,]/i);
  if (dataUrlMatch?.[1]) {
    return dataUrlMatch[1].toLowerCase();
  }

  const extension = toExtensionFromFileName(trimmed);
  return extension ? (IMAGE_CONTENT_TYPE_BY_EXTENSION[extension] ?? null) : null;
};

export const isSupportedImageContentType = (value: string | null | undefined): boolean => {
  const normalizedContentType = value?.trim().toLowerCase() ?? '';
  return Boolean(normalizedContentType && IMAGE_EXTENSION_BY_CONTENT_TYPE[normalizedContentType]);
};

export const isSupportedStillImageContentType = (value: string | null | undefined): boolean => {
  const normalizedContentType = value?.trim().toLowerCase() ?? '';
  return STILL_IMAGE_CONTENT_TYPES.has(normalizedContentType);
};

export const isSupportedImageFileName = (value: string | null | undefined): boolean => {
  const extension = toExtensionFromFileName(value);
  return Boolean(extension && IMAGE_CONTENT_TYPE_BY_EXTENSION[extension]);
};

export const isSupportedStillImageFileName = (value: string | null | undefined): boolean => {
  const extension = toExtensionFromFileName(value);
  if (!extension) {
    return false;
  }
  const contentType = IMAGE_CONTENT_TYPE_BY_EXTENSION[extension];
  return Boolean(contentType && STILL_IMAGE_CONTENT_TYPES.has(contentType));
};

export const inferImageExtension = ({
  fileName,
  contentType,
  fallback = 'png',
}: {
  fileName?: string | null;
  contentType?: string | null;
  fallback?: string;
}): string => {
  const normalizedContentType = contentType?.trim().toLowerCase() ?? '';
  if (normalizedContentType && IMAGE_EXTENSION_BY_CONTENT_TYPE[normalizedContentType]) {
    return IMAGE_EXTENSION_BY_CONTENT_TYPE[normalizedContentType];
  }

  const extension = toExtensionFromFileName(fileName);
  if (extension && IMAGE_CONTENT_TYPE_BY_EXTENSION[extension]) {
    return extension === 'jpeg' ? 'jpg' : extension;
  }

  const normalizedFallback = trimLeadingDot(fallback);
  return IMAGE_CONTENT_TYPE_BY_EXTENSION[normalizedFallback] ? normalizedFallback : 'png';
};

export const isStorageObjectPath = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    trimmed.startsWith(LEVEL_DESIGN_STORAGE_PREFIX) || trimmed.startsWith(SCENE_STORAGE_PREFIX)
  );
};

export const isLevelDesignStorageObjectPath = (value: string): boolean =>
  value.trim().startsWith(LEVEL_DESIGN_STORAGE_PREFIX);

export const isSceneStorageObjectPath = (value: string): boolean =>
  value.trim().startsWith(SCENE_STORAGE_PREFIX);

export const createLevelDesignStorageObjectPath = (
  userId: string,
  designId: string,
  assetId: string,
  extension: string,
): string =>
  `users/${userId.trim()}/level-designs/${designId.trim()}/assets/${sanitizeStorageAssetId(assetId)}.${trimLeadingDot(extension)}`;

export const createSceneBinaryAssetStorageObjectPath = (
  sceneId: string,
  slot: SceneBinaryAssetSlot,
  extension = 'png',
): string => {
  const backgroundExtension = trimLeadingDot(extension) === 'webp' ? 'webp' : 'png';
  const fileName =
    slot === 'background'
      ? `background.${backgroundExtension}`
      : slot === 'foreground'
        ? 'foreground.png'
        : slot === 'thumbnail'
          ? 'thumbnail.png'
          : 'traversal-mask.png';
  return `scenes/${sceneId.trim()}/${fileName}`;
};

export const createSceneAnimatedGifStorageObjectPath = (
  sceneId: string,
  assetId: string,
  extension: string,
): string =>
  `scenes/${sceneId.trim()}/animated-gifs/${sanitizeStorageAssetId(assetId, 'animated-gif')}.${trimLeadingDot(extension)}`;

export const isOwnedLevelDesignStorageObjectPath = (
  value: string,
  userId: string,
  designId: string,
): boolean => {
  const prefix = `users/${userId.trim()}/level-designs/${designId.trim()}/assets/`;
  return value.trim().startsWith(prefix);
};

export const isOwnedSceneStorageObjectPath = (value: string, sceneId: string): boolean =>
  value.trim().startsWith(`scenes/${sceneId.trim()}/`);
